import React from "react";
import { Box, Button, Typography } from "@mui/material";

/**
 * The card every Tier-1 widget sits in (canvas widget-mcp/Main `.rw`): a bordered surface under the
 * reply, a header (title, subtitle), a body and an optional footer of actions. Colours are the
 * widget's `--w-*` tokens only (chat-experience 16) — no per-widget styling (D-W30). The primary
 * button is filled with `--w-sender-bg` (white on it is 10:1; the accent is not AA for small text).
 */
export const WIDGET_SX = {
	width: "100%",
	boxSizing: "border-box",
	border: "1px solid var(--w-border)",
	borderRadius: "13px",
	background: "var(--w-surface)",
	color: "var(--w-text)",
	overflow: "hidden",
	fontSize: 13,
};

export const OPTION_SX = {
	border: "1px solid var(--w-border)",
	borderRadius: "9px",
	px: 1.25,
	py: 0.5,
	"&:has(input:checked)": { borderColor: "var(--w-accent)" },
	"&:has(input:focus-visible)": { outline: "2px solid var(--w-accent)", outlineOffset: 1 },
	"& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": { color: "var(--w-accent)" },
};

export function WidgetButton({ variant = "primary", children, ...props }) {
	const primary = variant === "primary";
	return (
		<Button
			size="small"
			disableElevation
			variant={primary ? "contained" : "outlined"}
			sx={{
				textTransform: "none",
				borderRadius: "9px",
				fontSize: 12,
				px: 1.75,
				...(primary
					? { background: "var(--w-sender-bg)", color: "var(--w-sender-color)", "&:hover": { background: "var(--w-sender-bg)", opacity: 0.9 } }
					: { color: "var(--w-text)", borderColor: "var(--w-border)", "&:hover": { borderColor: "var(--w-accent)" } }),
				"&.Mui-disabled": { opacity: 0.45, color: primary ? "var(--w-sender-color)" : "var(--w-text)" },
			}}
			{...props}
		>
			{children}
		</Button>
	);
}

export default function WidgetFrame({ kind, title, subtitle, titleId, headingRef, children, footer }) {
	return (
		<Box className="widget-native" data-widget-kind={kind} data-testid={`widget-${kind}`} sx={WIDGET_SX}>
			{(title || subtitle) && (
				<Box sx={{ px: 1.75, pt: 1.5, pb: 1 }}>
					{title && (
						<Typography id={titleId} ref={headingRef} tabIndex={headingRef ? -1 : undefined} component="p" sx={{ fontSize: 13, fontWeight: 500, outline: "none" }}>
							{title}
						</Typography>
					)}
					{subtitle && <Typography component="p" sx={{ fontSize: 11.5, color: "var(--w-text)" }}>{subtitle}</Typography>}
				</Box>
			)}
			<Box sx={{ px: 1.75, pb: 1.5, pt: title || subtitle ? 0 : 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
				{children}
			</Box>
			{footer && (
				<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, borderTop: "1px solid var(--w-border)", px: 1.75, py: 1.25 }}>
					{footer}
				</Box>
			)}
		</Box>
	);
}
